import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import React, { useState, useEffect, ChangeEvent } from 'react';
import cloneDeep from 'lodash.clonedeep';

import {
  useLearningJourneyDiscussionTeamsQueryRemote,
  useBootstrapLearningJourneySessionDiscussionTeamsMutationRemote,
  RLearningJourneySessionDiscussionTeam,
  RLearningJourneySessionDiscussionTeamMember,
} from '@/store/v2';
import { Button } from '@/components/Button/Button';
import { ViewTeamsList } from '@/administration/pages/Journey/pages/ViewTeams/ViewTeamsList';
import { Loader } from '@/component/Loader';

const UncreatedTeamsStyled = styled.div`
  display: flex;
  flex-direction: column;
  width: 380px;
  padding-left: 24px;
  padding-right: 24px;
`;

const UncreatedSubtitleStyled = styled.div`
  ${({ theme }) => theme.typography.body2};
  color: ${(p) => p.theme.colors.neutral.outline2};
  margin-bottom: 4px;
`;

const UncreatedDescriptionStyled = styled.div`
  ${({ theme }) => theme.typography.caption};
  color: ${(p) => p.theme.colors.neutral.outline2};
  margin-bottom: 24px;
`;

type ViewTeamsContentProps = {
  teams: RLearningJourneySessionDiscussionTeam[];
  setTeams: (teams: RLearningJourneySessionDiscussionTeam[]) => void;
  setIsResetMode: (isReset: boolean) => void;
  setSessionId: (id: number | null) => void;
  isResetMode: boolean;
  setIsSaveDisabled: (isDisabled: boolean) => void;
};

export const ViewTeamsContent = ({
  teams,
  setTeams,
  setSessionId,
  isResetMode,
  setIsResetMode,
  setIsSaveDisabled,
}: ViewTeamsContentProps): React.ReactElement => {
  const [teamsSize, setTeamsSize] = useState<number>(1);
  const { journeyId } = useParams<{ journeyId: string }>();

  useEffect(() => {
    const excessiveTeam = teams?.find((team) => {
      return team?.members?.length > teamsSize;
    });

    setIsSaveDisabled(!!excessiveTeam);
  }, [teams, teamsSize]);

  const [bootstrapTeams] = useBootstrapLearningJourneySessionDiscussionTeamsMutationRemote();
  const { data, loading } = useLearningJourneyDiscussionTeamsQueryRemote({
    variables: { id: Number(journeyId) },
    skip: !journeyId,
  });

  useEffect(() => {
    setTeams(data?.learningJourney?.sessions?.[0]?.discussionTeams as RLearningJourneySessionDiscussionTeam[]);
    setSessionId(data?.learningJourney?.sessions?.[0]?.id as number);
  }, [data]);

  const areDiscussionTeamsEstablished = !!data?.learningJourney?.sessions?.[0]?.discussionTeams?.length;

  const handleTeamSizeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTeamsSize(Number(event.target.value));
  };

  const handleSettingUpTeams = async () => {
    bootstrapTeams({
      variables: { teamSize: Number(teamsSize), sessionId: data?.learningJourney?.sessions?.[0]?.id as number },
      update(cache, { data }) {
        if (data?.bootstrapLearningJourneySesssionDiscussionTeams?.success) {
          cache.evict({ fieldName: 'learningJourney' });
        }
      },
    });

    setIsResetMode(false);
  };

  const handleTeamSwitch = ({ newTeamId, oldTeamId, memberId }: { newTeamId: number; oldTeamId: number; memberId: number }) => {
    const newTeams = cloneDeep(teams);
    let movedMember: RLearningJourneySessionDiscussionTeamMember;

    // Removing member from old team
    newTeams?.find((team, index) => {
      if (team.id === oldTeamId) {
        newTeams[index].members = newTeams[index].members?.filter((member) => {
          if (member?.user?.id === memberId) {
            movedMember = cloneDeep(member);
          }

          return member?.user?.id !== memberId;
        });

        return true;
      }
    });

    // Adding member to new team
    newTeams?.find((team, index) => {
      if (team.id === newTeamId) {
        newTeams[index].members.push(movedMember);
        return true;
      }
    });

    setTeams(newTeams);
  };

  if (loading) return <Loader />;

  if (!areDiscussionTeamsEstablished || isResetMode) {
    return (
      <UncreatedTeamsStyled>
        <UncreatedSubtitleStyled>Learning teams setup</UncreatedSubtitleStyled>
        <UncreatedDescriptionStyled>
          Learning teams are generated based on available learners and aim for a balanced distribution across user realms. The
          team size you select will stay fixed unless you reset the teams and start over.
        </UncreatedDescriptionStyled>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
          <div className="field has-addons">
            <div className="control">
              <Button $type="static">Pick team size</Button>
            </div>
            <div className="control">
              <div className="select">
                <select
                  className="select"
                  value={teamsSize}
                  onChange={handleTeamSizeChange as unknown as React.ChangeEventHandler<HTMLSelectElement>}
                >
                  {[...new Array(10)].map((_v, index) => (
                    <option key={index} value={index + 1}>
                      {index + 1}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="field">
            <Button onClick={handleSettingUpTeams}>Set up</Button>
          </div>
        </div>
      </UncreatedTeamsStyled>
    );
  }

  if (loading || !teams) return <div />;

  return <ViewTeamsList teams={teams} teamSize={teamsSize} onTeamSwitch={handleTeamSwitch} />;
};
